import { useMemo } from 'react';
import { SupplierQualification } from '../../lib/api';
import { Pagination } from '../../components/ui/pagination';
import { Circle } from 'lucide-react';
import { statusConfig } from './statusConfig';
import MatrixCell from './MatrixCell';

interface MatrixViewProps {
  filteredQualifications: SupplierQualification[];
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

/* ============ MATRIX VIEW ============ */
export default function MatrixView({
  filteredQualifications,
  currentPage,
  pageSize,
  onPageChange,
}: MatrixViewProps) {
  // Build matrix data: group qualifications by supplier
  const matrixData = useMemo(() => {
    const supplierMap = new Map<number, {
      id: number;
      name: string;
      qualifications: Map<number, SupplierQualification>;
    }>();

    filteredQualifications.forEach(qual => {
      const supplierId = qual.supplier;
      if (!supplierMap.has(supplierId)) {
        supplierMap.set(supplierId, {
          id: supplierId,
          name: qual.supplier_name || `Supplier ${supplierId}`,
          qualifications: new Map(),
        });
      }
      supplierMap.get(supplierId)!.qualifications.set(qual.template, qual);
    });

    return Array.from(supplierMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredQualifications]);

  // Get unique templates from qualifications for matrix columns
  const matrixTemplates = useMemo(() => {
    const templateMap = new Map<number, { id: number; name: string }>();
    filteredQualifications.forEach(qual => {
      if (!templateMap.has(qual.template)) {
        templateMap.set(qual.template, {
          id: qual.template,
          name: qual.template_name || `Template ${qual.template}`,
        });
      }
    });
    return Array.from(templateMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredQualifications]);

  // Pagination calculations for supplier rows
  const totalSuppliers = matrixData.length;
  const totalPages = Math.ceil(totalSuppliers / pageSize);
  const paginatedMatrixData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return matrixData.slice(startIndex, startIndex + pageSize);
  }, [matrixData, currentPage, pageSize]);

  return (
    <div className="space-y-4">
      {/* Matrix Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">
            Approval Register ({totalSuppliers} Suppliers)
          </h2>
          <p className="text-sm text-muted-foreground">
            Hover over circles for details, click to view qualification
          </p>
        </div>
        <span className="text-sm text-muted-foreground">
          {matrixTemplates.length} Questionnaire Types
        </span>
      </div>

      {/* Top Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalSuppliers}
          itemsPerPage={pageSize}
          onPageChange={onPageChange}
          itemLabel="suppliers"
        />
      )}

      {/* Matrix Table with horizontal scroll */}
      {/* Legend with colored circles - displayed above the table */}
      <div className="p-4 border rounded-lg bg-muted/20 mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          <span className="text-sm font-medium text-muted-foreground">Legend:</span>
          {Object.entries(statusConfig).map(([status, config]) => (
            <div key={status} className="flex items-center gap-2 text-sm">
              <Circle
                className="h-4 w-4"
                fill={config.dotColor}
                stroke={config.dotColor}
              />
              <span>{config.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>—</span>
            <span>Not Assigned</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto border rounded-lg shadow-sm">
        <table className="w-full border-collapse" style={{ minWidth: `${250 + matrixTemplates.length * 130}px` }}>
          <thead>
            <tr className="border-b-2 border-gray-300 bg-muted/50">
              <th className="p-3 text-left font-semibold text-sm sticky left-0 bg-muted/50 z-10 min-w-[250px] border-r border-gray-300">
                Supplier
              </th>
              {matrixTemplates.map(template => (
                <th
                  key={template.id}
                  className="p-3 text-center font-semibold text-sm min-w-[120px] max-w-[150px] border-l border-gray-200"
                  title={template.name}
                >
                  <div className="text-xs leading-tight break-words whitespace-normal">
                    {template.name}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedMatrixData.map((supplier, idx) => (
              <tr
                key={supplier.id}
                className={`border-b hover:bg-muted/30 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-muted/10'}`}
              >
                <td className="p-3 sticky left-0 bg-inherit z-10 border-r border-gray-300">
                  <div className="font-medium text-sm">{supplier.name}</div>
                </td>
                {matrixTemplates.map(template => {
                  const qual = supplier.qualifications.get(template.id);
                  return (
                    <td key={template.id} className="p-3 text-center border-l border-gray-100">
                      <MatrixCell qual={qual} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bottom Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalSuppliers}
          itemsPerPage={pageSize}
          onPageChange={onPageChange}
          itemLabel="suppliers"
        />
      )}

    </div>
  );
}
